import { useSelector } from 'react-redux';
import { tenantSelector } from '../selector/authenticationSelector';
import { isSelfServiceTenant } from '../common/tenantStore';
import { useGetAccountQuery } from '../api/account/accountApiSlice';
import { useIsMaster } from '../common/useIsMaster';

export interface AccountAccess {
    /** Owner (full edit) or master (read-only) of an onboarded tenant. */
    canAccess: boolean;
    /**
     * True while the role is still being resolved (self-service, non-master,
     * getAccount not settled yet). The route guard must WAIT on this rather
     * than redirect — otherwise an owner deep-linking to /account is bounced to
     * "unauthorized" before their role loads.
     */
    isResolving: boolean;
}

/**
 * Whether the current user may open the Account section for the selected
 * tenant. Onboarded (self-service) tenants only, and within those only:
 *   - the tenant OWNER (full edit), or
 *   - a MASTER viewing the tenant (read-only).
 *
 * Normal non-owner members do NOT get access. Legacy tenants never do (their
 * settings aren't managed through this screen). Drives both the sidebar nav
 * item and the `/account` route guard so they stay in sync.
 */
export const useCanAccessAccount = (): AccountAccess => {
    const tenant = useSelector(tenantSelector);
    const isSelfService = isSelfServiceTenant(tenant);
    const isMaster = useIsMaster();
    // getAccount resolves to the caller's own tenant + role. Skip it for
    // masters (their own role is irrelevant — they're covered by isMaster) and
    // for legacy / non-self-service users (no operational app_user row).
    const skip = !isSelfService || isMaster;
    const { data: account, isLoading, isFetching } = useGetAccountQuery(undefined, { skip });

    const isOwner = !!account && 'user' in account && account.user.role === 'owner';
    const canAccess = isSelfService && (isOwner || isMaster);
    // Only "resolving" while the role query is genuinely in flight; once it
    // settles (success or error) we trust `canAccess` and stop waiting.
    const isResolving = !skip && !account && (isLoading || isFetching);

    return { canAccess, isResolving };
};
